/**
 * Backup Codes Display Component (DC-904)
 *
 * Displays MFA backup codes with options to:
 * - Copy all codes to clipboard
 * - Download codes as a text file
 * - Print codes
 */

import React, { useState } from 'react';
import { BRANDING } from '../../config/branding';

interface BackupCodesDisplayProps {
  backupCodes: string[];
  onContinue: () => void;
  isInitialSetup?: boolean;
}

export function BackupCodesDisplay({
  backupCodes,
  onContinue,
  isInitialSetup = false,
}: BackupCodesDisplayProps) {
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleCopyAll = async () => {
    const codesText = backupCodes.join('\n');
    try {
      await navigator.clipboard.writeText(codesText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = codesText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    const content = `${BRANDING.productName} - MFA Backup Codes
================================

These are your one-time backup codes. Each code can only be used once.
Store them in a safe place. If you lose access to your authenticator app,
you can use one of these codes to sign in.

${backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

Generated: ${new Date().toISOString()}

IMPORTANT: Keep these codes secure! Anyone with these codes can access your account.
`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fair-compass-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${BRANDING.productName} - Backup Codes</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              padding: 2rem;
              max-width: 600px;
              margin: 0 auto;
            }
            h1 { font-size: 1.5rem; margin-bottom: 1rem; }
            .codes {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 0.5rem;
              margin: 1.5rem 0;
            }
            .code {
              font-family: monospace;
              font-size: 1.25rem;
              padding: 0.5rem;
              background: #f5f5f5;
              border-radius: 4px;
              text-align: center;
            }
            .warning {
              background: #fff3cd;
              border: 1px solid #ffc107;
              padding: 1rem;
              border-radius: 4px;
              margin-top: 1.5rem;
            }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <h1>${BRANDING.productName} - MFA Backup Codes</h1>
          <p>Each code can only be used once. Store them securely.</p>
          <div class="codes">
            ${backupCodes.map((code) => `<div class="code">${code}</div>`).join('')}
          </div>
          <div class="warning">
            <strong>Important:</strong> Keep these codes secure! Anyone with these codes can access your account.
          </div>
          <p style="color: #666; font-size: 0.875rem; margin-top: 1rem;">
            Generated: ${new Date().toLocaleString()}
          </p>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <div className="mfa-backup-codes">
      <h3 className="mfa-setup-title">
        {isInitialSetup ? 'Save Your Backup Codes' : 'Your New Backup Codes'}
      </h3>
      <p className="mfa-setup-subtitle">
        {isInitialSetup
          ? 'Save these backup codes in a safe place. If you lose access to your authenticator app, you can use one of these codes to sign in.'
          : 'Your previous backup codes are no longer valid. Save these new codes securely.'}
      </p>

      <div className="mfa-backup-warning">
        <span className="mfa-backup-warning-icon">!</span>
        <span>
          <strong>Important:</strong> Each code can only be used once. Store them securely.
        </span>
      </div>

      <div className="mfa-backup-codes-grid">
        {backupCodes.map((code, index) => (
          <div key={index} className="mfa-backup-code">
            <span className="mfa-backup-code-number">{index + 1}.</span>
            <code className="mfa-backup-code-value">{code}</code>
          </div>
        ))}
      </div>

      <div className="mfa-backup-actions">
        <button
          type="button"
          className="mfa-backup-action-button"
          onClick={handleCopyAll}
        >
          {copied ? (
            <>
              <span className="mfa-icon">&#10003;</span> Copied!
            </>
          ) : (
            <>
              <span className="mfa-icon">&#128203;</span> Copy All
            </>
          )}
        </button>
        <button
          type="button"
          className="mfa-backup-action-button"
          onClick={handleDownload}
        >
          <span className="mfa-icon">&#8595;</span> Download
        </button>
        <button
          type="button"
          className="mfa-backup-action-button"
          onClick={handlePrint}
        >
          <span className="mfa-icon">&#128424;</span> Print
        </button>
      </div>

      {isInitialSetup && (
        <div className="mfa-backup-confirm">
          <label className="mfa-backup-confirm-label">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mfa-backup-confirm-checkbox"
            />
            <span>I have saved my backup codes in a secure location</span>
          </label>
        </div>
      )}

      <div className="mfa-setup-actions">
        <button
          type="button"
          className="auth-button"
          onClick={onContinue}
          disabled={isInitialSetup && !confirmed}
        >
          {isInitialSetup ? 'Complete Setup' : 'Done'}
        </button>
      </div>
    </div>
  );
}

export default BackupCodesDisplay;
